// MCHD Room Scheduler - API Interceptors
// Handles automatic token refresh and request/response interception

import { refreshToken } from './endpoints/auth';
import { HTTP_STATUS, API_CONFIG } from './index';
import { retryWithBackoff, DEFAULT_AUTH_RETRY_CONFIG, createNetworkError, NetworkHealthChecker } from '../utils/network-resilience';

/**
 * Token management utilities
 */
export class TokenManager {
  private static refreshPromise: Promise<string | null> | null = null;

  /**
   * Get stored access token
   * @returns Access token or null
   */
  static getAccessToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Get stored refresh token
   * @returns Refresh token or null
   */
  static getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  /**
   * Store access token with expiration
   * @param token - Access token
   * @param expiresIn - Token expiration time in seconds
   */
  static storeAccessToken(token: string, expiresIn: string): void {
    localStorage.setItem('auth_token', token);
    
    // Calculate expiration timestamp
    const expirationTime = Date.now() + (this.parseExpirationTime(expiresIn) * 1000);
    localStorage.setItem('token_expires_at', expirationTime.toString());
  }

  /**
   * Store refresh token
   * @param token - Refresh token
   */
  static storeRefreshToken(token: string): void {
    localStorage.setItem('refresh_token', token);
  }

  /**
   * Check if token is expired or will expire soon (within 2 minutes)
   * @returns True if token needs refresh
   */
  static needsRefresh(): boolean {
    const expirationTime = localStorage.getItem('token_expires_at');
    if (!expirationTime) return true;
    
    const now = Date.now();
    const expiresAt = parseInt(expirationTime, 10);
    const twoMinutes = 2 * 60 * 1000; // 2 minutes in milliseconds
    
    return now >= (expiresAt - twoMinutes);
  }

  /**
   * Clear all tokens
   */
  static clearTokens(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
  }

  /**
   * Parse expiration time string to seconds
   * @param expiresIn - Expiration string (e.g., "15m", "1h", "7d")
   * @returns Seconds
   */
  private static parseExpirationTime(expiresIn: string): number {
    const unit = expiresIn.slice(-1);
    const value = parseInt(expiresIn.slice(0, -1), 10);
    
    switch (unit) {
      case 'm': return value * 60;
      case 'h': return value * 60 * 60;
      case 'd': return value * 24 * 60 * 60;
      default: return value;
    }
  }

  /**
   * Refresh access token using refresh token
   * @returns New access token or null if refresh failed
   */
  static async refreshAccessToken(): Promise<string | null> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshTokenValue = this.getRefreshToken();
    if (!refreshTokenValue) {
      return null;
    }

    this.refreshPromise = this.performRefresh(refreshTokenValue);
    
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Perform the actual token refresh
   * @param refreshTokenValue - Refresh token
   * @returns New access token or null
   */
  private static async performRefresh(refreshTokenValue: string): Promise<string | null> {
    try {
      const response = await refreshToken(refreshTokenValue);
      
      if (response.success && response.data) {
        this.storeAccessToken(response.data.token, response.data.expiresIn);
        this.storeRefreshToken(response.data.refreshToken);
        return response.data.token;
      }
      
      return null;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return null;
    }
  }
}

/**
 * Request interceptor to add authorization header
 * @param config - Request configuration
 * @returns Modified configuration
 */
export function requestInterceptor(config: any): any {
  const token = TokenManager.getAccessToken();
  
  if (token) {
    config.headers = {
      ...config.headers,
      'Authorization': `Bearer ${token}`,
    };
  }
  
  return config;
}

/**
 * Response interceptor to handle 401 errors and token refresh
 * @param response - Response object
 * @returns Response or retried request
 */
export async function responseInterceptor(response: any): Promise<any> {
  // If response is successful, return as is
  if (response.status < 400) {
    return response;
  }

  // Handle 401 Unauthorized
  if (response.status === HTTP_STATUS.UNAUTHORIZED) {
    const originalRequest = response.config;
    
    // Skip retry if this is already a retry attempt
    if (originalRequest._retry) {
      await performProperLogout();
      return Promise.reject(response);
    }

    // Check if we have a refresh token
    const refreshTokenValue = TokenManager.getRefreshToken();
    if (!refreshTokenValue) {
      await performProperLogout();
      return Promise.reject(response);
    }

    try {
      // Attempt to refresh token
      const newToken = await TokenManager.refreshAccessToken();
      
      if (newToken) {
        // Retry original request with new token
        originalRequest._retry = true;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
        // Retry the request
        return fetch(originalRequest.url, {
          method: originalRequest.method,
          headers: originalRequest.headers,
          body: originalRequest.data,
          credentials: 'include',
        }).then(res => res.json());
      } else {
        // Refresh failed, perform proper logout
        await performProperLogout();
        return Promise.reject(response);
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      await performProperLogout();
      return Promise.reject(response);
    }
  }

  return response;
}

/**
 * Perform proper logout with backend session cleanup and retry logic
 * @returns Promise<void>
 */
async function performProperLogout(): Promise<void> {
  try {
    // Check network health first
    const healthChecker = NetworkHealthChecker.getInstance();
    const isHealthy = await healthChecker.isHealthy();
    
    if (!isHealthy) {
      console.warn('Network is not healthy, skipping logout API call');
    } else {
      // Attempt to call logout API to clean up backend sessions with retry
      const token = TokenManager.getAccessToken();
      if (token) {
        try {
          await retryWithBackoff(
            async () => {
              const response = await fetch(`${API_CONFIG.BASE_URL}/auth/logout`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                credentials: 'include',
                signal: AbortSignal.timeout(5000), // 5 second timeout
              });
              
              if (!response.ok) {
                throw createNetworkError(new Error(`Logout failed with status: ${response.status}`));
              }
              
              return response;
            },
            {
              ...DEFAULT_AUTH_RETRY_CONFIG,
              maxAttempts: 2, // Fewer retries for logout
              baseDelay: 500, // Faster retry for logout
            },
            'logout'
          );
        } catch (logoutError) {
          console.warn('Logout API call failed after retries, but continuing with cleanup:', logoutError);
          // Continue with cleanup even if logout API fails
        }
      }
    }
  } catch (error) {
    console.warn('Error during proper logout:', error);
  } finally {
    // Always clear local tokens and redirect
    TokenManager.clearTokens();
    window.location.href = '/login';
  }
}

/**
 * Error interceptor for handling network errors
 * @param error - Error object
 * @returns Rejected promise
 */
export function errorInterceptor(error: any): Promise<any> {
  if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
    performProperLogout();
  }
  
  return Promise.reject(error);
}

/**
 * Setup proactive token refresh timer
 * Checks token expiration every minute and refreshes if needed
 */
export function setupProactiveRefresh(): void {
  setInterval(async () => {
    const token = TokenManager.getAccessToken();
    if (!token) return;

    if (TokenManager.needsRefresh()) {
      console.log('Token needs refresh, attempting proactive refresh...');
      const newToken = await TokenManager.refreshAccessToken();
      
      if (!newToken) {
        console.warn('Proactive token refresh failed, user will be redirected on next API call');
      } else {
        console.log('Token refreshed successfully');
      }
    }
  }, 60000); // Check every minute
}

/**
 * Enhanced fetch function with automatic token management
 * @param url - Request URL
 * @param options - Fetch options
 * @returns Promise with response
 */
export async function authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  // Add authorization header
  const token = TokenManager.getAccessToken();
  if (token) {
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    };
  }

  // Make request
  let response = await fetch(url, options);

  // Handle 401 responses
  if (response.status === HTTP_STATUS.UNAUTHORIZED) {
    const refreshTokenValue = TokenManager.getRefreshToken();
    if (refreshTokenValue) {
      try {
        const newToken = await TokenManager.refreshAccessToken();
        if (newToken) {
          // Retry with new token
          options.headers = {
            ...options.headers,
            'Authorization': `Bearer ${newToken}`,
          };
          response = await fetch(url, options);
        } else {
          // Refresh failed, perform proper logout
          await performProperLogout();
          throw new Error('Authentication failed');
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        await performProperLogout();
        throw error;
      }
    } else {
      // No refresh token, perform proper logout
      await performProperLogout();
      throw new Error('Authentication required');
    }
  }

  return response;
}
